/**
 * Service Container for dependency injection
 * Replaces global window variable pattern with proper service management
 * Updated to use standardized error handling
 */

import { ServiceError } from './error-handling';

export interface ServiceContainer {
  register<T>(key: string, service: T): void;
  get<T>(key: string): T;
  has(key: string): boolean;
  clear(): void;
}

export class DefaultServiceContainer implements ServiceContainer {
  private services = new Map<string, any>();

  register<T>(key: string, service: T): void {
    this.services.set(key, service);
  }

  get<T>(key: string): T {
    const service = this.services.get(key);
    if (!service) {
      throw new ServiceError('ServiceContainer', 'get', `Service '${key}' not found in container`);
    }
    return service;
  }

  has(key: string): boolean {
    return this.services.has(key);
  }

  clear(): void {
    this.services.clear();
  }
}

// Create global container instance
export const container = new DefaultServiceContainer();

// Service keys for type safety
export const ServiceKeys = {
  AUDIO_ENGINE: 'audioEngine',
  STATE_MANAGER: 'stateManager',
  DATA_MANAGER: 'dataManager',
  CONFIG_MANAGER: 'configManager',
  EQ_PROCESSOR: 'eqProcessor',
  GRAPH_RENDERER: 'graphRenderer',
  UI_CONTROLLER: 'uiController',
  EVENT_HANDLERS: 'eventHandlers',
  NOTIFICATION_SYSTEM: 'notificationSystem',
  MODAL_DIALOG: 'modalDialog',
  EQ_EXPORTER: 'eqExporter',
  LOGGER: 'logger',
  ENVIRONMENT: 'environment',
  MATH_UTILS: 'mathUtils'
} as const;

export type ServiceKey = typeof ServiceKeys[keyof typeof ServiceKeys];